import { Form, Formik }         from 'formik';
import { useCallback, useMemo } from 'react';
import styled                   from 'styled-components';
import { Button, Grid }         from '@mui/material';

import { FormPart }         from '@modules/shared/components/FormPart';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { ECheckType }       from '@shared/interfaces';
import { Modal }            from '@shared/components/modals/Modal';
import { base64ToUint8Array, handleDownloadPdf, formatDate } from '@shared/commonFunctions';
import { ECheckRequest, ICheckRequest } from './interfaces';
import { requestPDF }       from './requests';

const ButtonWrapper = styled.div`
  display         : flex;
  justify-content : flex-end;
`;

const baseConfig = [
  { name: 'requestedBy' },
  { name: 'requestedDate' },
  { name: 'customerName' },
  { name: 'loanConsultant' },
  { name: 'payeeName' },
  {
      name         : 'amountRequested',
      label        : 'Amount Requested',
      prefix       : '$',
      decimalScale : 2,
      component    : NumberInputField
  }, 
  { name: 'reason' },
  { name: 'assetDesc', label: 'Related Asset' },
  { name: 'statusCD', label: 'Status' },
  { name: 'requestComment' },
];

interface ICheckRequestModalProps {
    open             : boolean;
    modalType        : ECheckType;
    checkRequestType : ECheckRequest;
    requestRowData   : ICheckRequest | null;
    checkRequestModalPosition: number;
    handleClose      : () => void;
}

const productCheckRequestsFields = [
  { name: 'customerRefundAmount' },
  { name: 'sellerRefundAmount' },
  { name: 'cancellationDate' },
  { name: 'achRefNumber', label: 'ACH Ref Number' },
  { name: 'productType' },
  { name: 'provider' },
];

const decisionCommentField = [{
  name  : 'decisionComment',
  label : 'Decision Comment (Required or Decline)',
  width : '100%',
}];

export const CheckRequestModal = ({
    open,
    modalType,
    checkRequestType,
    requestRowData,
    checkRequestModalPosition,
    handleClose,
}: ICheckRequestModalProps) => {
  const initialValues = useMemo(() => ({
    achRefNumber         : requestRowData?.achRefNumber || '',
    customerRefundAmount : requestRowData?.customerRefundAmount || null,
    cancellationDate     : requestRowData?.requestedDate ? formatDate(requestRowData.requestedDate) : '',
    productType          : requestRowData?.productType || '',
    provider             : requestRowData?.provider || '',
    decisionComment      : requestRowData?.decisionComment || '',
    requestedBy          : requestRowData?.requestedBy || '',
    requestedDate        : requestRowData?.requestedDate ? formatDate(requestRowData.requestedDate) : '',
    customerName         : requestRowData?.customerName || '',
    loanConsultant       : requestRowData?.loanConsultant || '',
    payeeName            : requestRowData?.payeeName || '',
    amountRequested      : requestRowData?.amountRequested || '',
    reason               : requestRowData?.reason || null,
    assetDesc            : requestRowData?.assetDesc || '',
    statusCD             : requestRowData?.statusCD || null,
    requestComment       : requestRowData?.requestComment || null,
    sellerRefundAmount   : requestRowData?.sellerRefundAmount || null                 
  }), [requestRowData]);

  const extendedFormConfig = useMemo(() => {
    switch (modalType) {
      case ECheckType.Requests:
        return [...baseConfig, ...decisionCommentField];
      case ECheckType.ProductRequests:
        return [...baseConfig, ...productCheckRequestsFields, ...decisionCommentField];
      case ECheckType.ProductRequestsCut:
      case ECheckType.ProductRequestsDeclined:
        return [...baseConfig, ...productCheckRequestsFields];
      default:
        return baseConfig;
    }
  }, [modalType]);

  const formattedFormConfig = useMemo(() => extendedFormConfig.map((field) => ({
    disabled : true,
    width    : '50%',
    minWidth : '100px',
    ...field
  })), [extendedFormConfig]);

  const downloadPdf = useCallback(
    async() => {
      if (requestRowData?.requestId) {
        const unitArray = await requestPDF(requestRowData.requestId, checkRequestType);

        if (unitArray) {
          const bytes = base64ToUint8Array(unitArray);
          handleDownloadPdf(bytes, requestRowData?.requestNumericId);
        }
      }
    }, 
    [requestPDF, checkRequestType, requestRowData?.requestId, requestRowData?.requestNumericId]
  );

  return (
    <Modal
      hideBackdrop
      dialogWidth = "600px"
      open = {open}
      resetPosition={checkRequestModalPosition}
      marginTop="45px"
      title={`Check request details for check id [${requestRowData?.requestNumericId || ''}]`}
      onClose = {handleClose}
    >
      <Grid container spacing={12}>
        <Grid item md={12}>
          <Formik initialValues={initialValues} onSubmit={handleClose}>
            <Form>
              <FormPart config = {formattedFormConfig} />

              <ButtonWrapper>
                <Button variant="contained" onClick={downloadPdf}>
                  Download PDF
                </Button>
              </ButtonWrapper>
            </Form>
          </Formik>
        </Grid>
      </Grid>
    </Modal>
  );
};
